import React, { useState, useEffect } from 'react';
import ProfileModal from './StudentProfileModal';
import CourseBatchModal from './CourseBatchModal';
import PaymentDetailsModal from './PaymentDetailsModal';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './module.css';
import AttendanceCalender from './AttendanceCalender';
import { useNavigate } from 'react-router-dom';
import { Grid, Card, CardContent, Typography, Box } from '@mui/material';
import { Container } from 'react-bootstrap';
import Modal from '@mui/material/Modal';

// Clients that should not see any fee-related UI
const HIDE_FEE_CLIENTS = ["PTSS", "HBIS"];

function StdDashboard() {
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showCourseBatchModal, setShowCourseBatchModal] = useState(false);
  const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
  const [openFeeModal, setOpenFeeModal] = useState(false);
  const [dueMessage, setDueMessage] = useState("");
  // Read initially from sessionStorage so the fee card doesn't flash on first render
  const [clientCode, setClientCode] = useState(sessionStorage.getItem("clientCode") || "");

  // true for PTSS and HBIS (case-insensitive)
  const hideFees = HIDE_FEE_CLIENTS.includes((clientCode || "").toUpperCase());

  let navigate = useNavigate();

  const handleOpenProfileModal = () => setShowProfileModal(true);
  const handleCloseProfileModal = () => setShowProfileModal(false);
  const handleOpenCourseBatchModal = () => setShowCourseBatchModal(true);
  const handleCloseCourseBatchModal = () => setShowCourseBatchModal(false);
  const handleOpenPaymentDetailsModal = () => setShowPaymentDetailsModal(true);
  const handleClosePaymentDetailsModal = () => setShowPaymentDetailsModal(false);
  const handleAssignmentClick = () => navigate('/assignmentlist');
  const handleStudyMaterialClick = () => navigate('/subjects');

  const cardGradient = 'linear-gradient(135deg, #080F1F 0%, #102A43 55%, #1B4965 100%)';

  useEffect(() => {
    // Get clientCode from sessionStorage
    const storedClientCode = sessionStorage.getItem("clientCode");
    if (storedClientCode) {
      setClientCode(storedClientCode);
    }
  }, []);

  useEffect(() => {
    // Don't fetch fee details for clients without fees (PTSS, HBIS)
    if (hideFees) {
      return;
    }

    const fetchFeeDetails = async () => {
      try {
        const studentID = sessionStorage.getItem("employeeId");
        const token = sessionStorage.getItem("token");

        if (!studentID || !token) {
          return;
        }

        const apiUrl = "https://wsmsv1.aidigitalsec.com/api/Student/FetchStudentPaymentDetails";

        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ studentID }),
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch fee details: ${response.status}`);
        }

        const data = await response.json();
        const overdueFees = data.filter((fee) => fee.paymentStatus === "Overdue");

        if (overdueFees.length > 0) {
          const totalDue = overdueFees.reduce((sum, fee) => sum + (fee.dueAmount || 0), 0);
          const months = overdueFees.map((fee) => fee.paymentForMonth || "Unknown").join(", ");
          setDueMessage(
            `Fee for ${months} is overdue. Total due: ₹${totalDue.toFixed(2)}`
          );
          setOpenFeeModal(true);
        }
      } catch (error) {
        console.error("Error fetching fee details:", error);
      }
    };

    fetchFeeDetails();
  }, [clientCode, hideFees]);

  // Function to render cards conditionally
  const renderCards = () => {
    const cards = [
      {
        id: 1,
        title: "Profile",
        background: 'linear-gradient(135deg, #080F1F 0%, #102A43 55%, #1B4965 100%)',
        hoverShadow: '0 8px 16px rgba(69, 150, 164, 0.3)',
        onClick: handleOpenProfileModal,
        showModal: showProfileModal,
        modalComponent: <ProfileModal onClose={handleCloseProfileModal} />
      },
      {
        id: 2,
        title: "Class and Subject",
        background: 'linear-gradient(111.1deg, rgba(199, 89, 5, 0.9), rgba(243, 179, 82, 0.9))',
        hoverShadow: '0 8px 16px rgba(199, 89, 5, 0.3)',
        onClick: handleOpenCourseBatchModal,
        showModal: showCourseBatchModal,
        modalComponent: <CourseBatchModal onClose={handleCloseCourseBatchModal} />
      },
      // Fee Details card - hidden for PTSS and HBIS
      ...(!hideFees ? [{
        id: 3,
        title: "Fee Details",
        background: 'linear-gradient(111.1deg, rgba(43, 140, 16, 0.9), rgba(78, 210, 111, 0.9))',
        hoverShadow: '0 8px 16px rgba(43, 140, 16, 0.3)',
        onClick: handleOpenPaymentDetailsModal,
        showModal: showPaymentDetailsModal,
        modalComponent: <PaymentDetailsModal onClose={handleClosePaymentDetailsModal} />
      }] : []),
      {
        id: 4,
        title: "Assignment",
        background: 'linear-gradient(111.1deg, rgba(22, 63, 151, 0.9), rgba(7, 17, 91, 0.9))',
        hoverShadow: '0 8px 16px rgba(22, 63, 151, 0.3)',
        onClick: handleAssignmentClick,
        showModal: false,
        modalComponent: null
      },
      {
        id: 5,
        title: "Study Material",
        background: 'linear-gradient(111.1deg, rgba(13, 173, 173, 0.9), rgba(18, 216, 216, 0.9))',
        hoverShadow: '0 8px 16px rgba(13, 173, 173, 0.3)',
        onClick: handleStudyMaterialClick,
        showModal: false,
        modalComponent: null
      }
    ];

    return cards.map((card) => (
      <Grid item xs={12} sm={6} md={4} lg={2.4} key={card.id}>
        <Card
          sx={{
            background: card.background,
            color: 'white',
            cursor: 'pointer',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: card.hoverShadow,
            },
            borderRadius: 2,
            height: '100%',
          }}
          onClick={card.onClick}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{card.title}</Typography>
          </CardContent>
        </Card>
        {card.showModal && card.modalComponent}
      </Grid>
    ));
  };

  return (
    <Container>
      <Box sx={{ my: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 3 }}>
          <i className="fas fa-tachometer-alt me-2"></i> Dashboard
        </Typography>

        {/* Single Row of Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {renderCards()}
        </Grid>

        {/* Attendance Calendar Section */}
        <Box sx={{ mt: 4 }}>
          <AttendanceCalender />
        </Box>
      </Box>

      {/* Fee Overdue Reminder Modal (appears on dashboard load if overdue) */}
      {/* Hidden for PTSS and HBIS */}
      {!hideFees && (
        <Modal
          open={openFeeModal}
          aria-labelledby="fee-modal-title"
          aria-describedby="fee-modal-description"
          sx={{
            backdropFilter: "blur(8px)",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            "& .MuiModal-backdrop": {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: '90%', sm: 420 },
              maxWidth: 440,
              bgcolor: "background.paper",
              borderRadius: 3,
              boxShadow: "0 20px 60px rgba(220, 38, 38, 0.3)",
              overflow: "hidden",
              outline: 'none',
              border: "2px solid #ef4444",
            }}
          >
            {/* Warning Icon Section with Red Gradient */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)",
                py: 3,
                px: 3,
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Decorative circles */}
              <Box
                sx={{
                  position: "absolute",
                  top: -15,
                  right: -15,
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: "rgba(255, 255, 255, 0.08)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: -20,
                  left: -20,
                  width: 90,
                  height: 90,
                  borderRadius: "50%",
                  bgcolor: "rgba(255, 255, 255, 0.08)",
                }}
              />

              {/* Warning Icon */}
              <Box
                sx={{
                  position: "relative",
                  width: 70,
                  height: 70,
                  borderRadius: "50%",
                  bgcolor: "rgba(255, 255, 255, 0.2)",
                  border: "3px solid rgba(255, 255, 255, 0.4)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px",
                  animation: "pulse 2s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%, 100%": { transform: "scale(1)", boxShadow: "0 0 0 0 rgba(255, 255, 255, 0.4)" },
                    "50%": { transform: "scale(1.05)", boxShadow: "0 0 0 8px rgba(255, 255, 255, 0)" },
                  },
                }}
              >
                <svg
                  width="36"
                  height="36"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </Box>

              <h2
                id="fee-modal-title"
                style={{
                  margin: 0,
                  color: "white",
                  fontSize: "22px",
                  fontWeight: 700,
                  textAlign: "center",
                  letterSpacing: "-0.3px",
                }}
              >
                Fee Payment Overdue
              </h2>
            </Box>

            {/* Content Section */}
            <Box sx={{ px: 3, pt: 3, pb: 3 }}>
              {/* Warning Badge */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  bgcolor: "#fee2e2",
                  color: "#991b1b",
                  px: 2,
                  py: 0.75,
                  borderRadius: 2,
                  fontSize: "12px",
                  fontWeight: 600,
                  margin: "0 auto 16px",
                  display: "flex",
                  justifyContent: "center",
                  width: "fit-content",
                  marginLeft: "auto",
                  marginRight: "auto",
                  border: "1px solid #fecaca",
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
                URGENT ACTION REQUIRED
              </Box>

              <p
                id="fee-modal-description"
                style={{
                  margin: "0 0 24px 0",
                  color: "#666",
                  fontSize: "14px",
                  lineHeight: "1.6",
                  textAlign: "center",
                }}
              >
                {dueMessage}
              </p>

              {/* Action Buttons */}
              <Box sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
                <button
                  onClick={() => setOpenFeeModal(false)}
                  style={{
                    width: "100%",
                    padding: "13px 24px",
                    background: "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    fontSize: "15px",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    boxShadow: "0 4px 12px rgba(220, 38, 38, 0.4)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 20px rgba(220, 38, 38, 0.5)";
                    e.currentTarget.style.background = "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(220, 38, 38, 0.4)";
                    e.currentTarget.style.background = "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)";
                  }}
                >
                  OKAY
                </button>
              </Box>
            </Box>

            {/* Bottom Red Accent Line */}
            <Box
              sx={{
                height: 3,
                background: "linear-gradient(90deg, #dc2626 0%, #ef4444 50%, #dc2626 100%)",
              }}
            />
          </Box>
        </Modal>
      )}
    </Container>
  );
}

export default StdDashboard;